import React from 'react';
import ProductCard from '../../../../components/Dashboard/catalog/ProductCard/ProductCard';
import { ProductCardSkeleton } from '../../../../components/Dashboard/catalog/ProductCard/ProductCardSkeleton';
import { IonContent, IonPage, IonSearchbar } from '@ionic/react';
import { IonicButton, IonicInput } from 'ionic-ui';
import PageTitle from '../../../../components/Dashboard/PageTitle';
import { Plus } from 'lucide-react';
import { useDialog } from '../../../../context/Dialog';
import AddNewProductDialog from '../../../../components/Dashboard/catalog/AddNewProductDialog/AddNewProductDialog';
import { dashboardPageStyle } from '../../Layout';
import Pagination from '../../../../components/Dashboard/Paginator';
import EmptyPage from '../../../../components/ui/EmptyPage';
import useCatalog from '../../../../services/Products/hooks/useCatalog';
import debounceFn from '../../../../utils/helperFunction/debounceFn';
import { PRODUCTS_QUANTITY } from '../../../../Constants/productConstants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Link } from 'react-router-dom';
import { ActiveFilterBar } from '../../../../components/ui/filterBar/ActiveFilterBar';
import ErrorBanner from '../../../../components/ui/ErrorBanner';

const placeholderProducts = new Array(4).fill(0);

export default function index() {
  const url = new URLSearchParams(window.location.search);
  const currentPage = url.get('page') ? Number(url.get('page')) : 1;
  const currentCategory = url.get('category') || undefined;
  const currentQuantity = url.get('quantity') || undefined;
  const { openDialog } = useDialog();
  const handleNewProduct = () => {
    openDialog(<AddNewProductDialog />, { allowBackgroundDismiss: false });
  };
  const { hasStore } = useSelector((state: RootState) => state.userStores);

  const {
    catalog,
    isError,
    isLoading,
    isPending,
    refetch,
    totalPage,
    availableCategories,
    applyFilter,
    activeFilters,
    filter,
  } = useCatalog({ currentPage, currentCategory, currentQuantity });

  const handleSearch = debounceFn(({ target }: any) => {
    applyFilter('search', target.value);
  }, 1000);

  return (
    <IonPage>
      <IonContent>
        <div className={dashboardPageStyle}>
          <div className="high-emphasis-text space-y-2">
            <PageTitle>Catalog</PageTitle>
            <p className="medium-emphasis-text">
              📈 You're managing {catalog?.pagination?.total_count || 0} active
              products. Keep an eye on low stock items to avoid missed orders.
            </p>
          </div>
          <div className="space-y-2">
            {/* FILTER BAR  */}
            <div className="w-full flex max-lg:flex-col gap-4 justify-between flex-wrap lg:items-center">
              <div className="flex-1 min-w-44 md:max-w-80 max-md:w-full">
                <IonSearchbar
                  placeholder="Search"
                  value={filter.search}
                  disabled={!hasStore}
                  style={{
                    '--box-shadow': 'none',
                    '--background': 'transparent',
                  }}
                  onIonInput={handleSearch}
                  className="rounded-md text-sm p-0 !h-10 font-dm_sans border border-border font-light text-text-dark-high"
                />
              </div>
              <div className="flex items-stretch max-lg:w-full flex-wrap gap-2">
                <div className="flex-1 min-w-fit sm:min-w-40">
                  <IonicInput
                    type="select"
                    disabled={!hasStore}
                    placeholder="Categories"
                    labelPlacement="start"
                    value={filter.category || 'all'}
                    containerClass="!gap-2 items-center h-full w-full flex-1 !flex-row bg-gray-50 rounded-md"
                    innerContainerClass="max-h-full h-full !w-full"
                    className="h-9 w-full  medium-emphasis-text font-medium"
                    labelClass="!m-0"
                    onChange={({ target }: any) =>
                      applyFilter('category', target.value)
                    }
                    options={availableCategories}
                  />
                </div>
                <div className="flex-1 min-w-fit sm:min-w-40 relative">
                  <IonicInput
                    type="select"
                    disabled={!hasStore}
                    placeholder="status"
                    containerClass="!gap-2 w-full items-center h-full !flex-row bg-gray-50 flex-1 rounded-md"
                    innerContainerClass="max-h-full h-full !w-full"
                    className="h-9 w-full medium-emphasis-text font-medium"
                    labelClass="!m-0"
                    value={filter.quantity ?? 'all'}
                    onChange={({ target }: any) =>
                      applyFilter('quantity', target.value)
                    }
                    options={[
                      {
                        label: 'All Status',
                        value: 'all',
                        disabled: false,
                      },
                      {
                        label: 'In stock',
                        value: `${PRODUCTS_QUANTITY.IN_STOCK}`,
                        disabled: false,
                      },
                      {
                        label: 'Low on stock',
                        value: `${PRODUCTS_QUANTITY.LOW_ON_STOCK}`,
                        disabled: false,
                      },
                      {
                        label: 'Out of stock',
                        value: `${PRODUCTS_QUANTITY.OUT_OF_STOCK}`,
                        disabled: false,
                      },
                    ]}
                  />
                </div>
                <IonicButton
                  onClick={handleNewProduct}
                  disabled={!hasStore}
                  size="lg"
                  className="min-w-fit flex-1 flex items-center justify-center gap-1"
                >
                  <span>
                    <Plus className="size-4" />
                  </span>
                  <span>Add new product</span>
                </IonicButton>
              </div>
            </div>
            {/*ACTIVE FILTERS BAR */}
            {activeFilters.length > 0 && (
              <ActiveFilterBar
                resultCount={catalog?.pagination?.total_count || 0}
                filterKeywords={activeFilters}
              />
            )}
          </div>

          {/* PRODUCTS CONTAINER */}
          {(!isError && !isPending) || isLoading ? (
            <div className="grid gap-4 md:grid-cols-3 sm:grid-cols-2">
              {isLoading ? (
                placeholderProducts.map((_, index) => (
                  <ProductCardSkeleton key={`dummy-product-${index}`} />
                ))
              ) : catalog?.data && catalog?.data?.length > 0 ? (
                catalog?.data.map((pdt, index) => (
                  <ProductCard key={`real-product-${index}`} {...pdt} />
                ))
              ) : (
                <ErrorBanner
                  message="No Products in catalog"
                  className="sm:col-span-2 md:col-span-3 h-[40vh]"
                  buttonLabel="Add New Product"
                  retry={handleNewProduct}
                />
              )}
            </div>
          ) : !hasStore ? (
            <EmptyPage
              message="It looks like you haven't created a store yet."
              className=" h-[40vh]"
            >
              <Link
                to="/dashboard/get-started"
                className="text-primary w-fit mx-auto p-4 font-medium"
              >
                Create Store
              </Link>
            </EmptyPage>
          ) : (
            <ErrorBanner
              retry={refetch}
              message="Something went wrong"
              className=" h-[40vh]"
            />
          )}
          <Pagination totalPages={totalPage || 0} />
        </div>
      </IonContent>
    </IonPage>
  );
}
